/**
 * Логика отображения нагрузки по упражнению (части тела, группы мышц, раскрытие).
 * Используется в ExerciseSetBlock и др.
 */
const FULL_BODY_PART_ID = 'full_body';

export function getFilledBars(loadValue) {
  const load = Number.parseFloat(loadValue ?? 0);
  const normalized = Number.isFinite(load) ? Math.min(100, Math.max(0, load)) : 0;
  return Math.min(3, Math.max(0, Math.round(normalized / (100 / 3))));
}

export function getFilledBodyBars(loadValue) {
  const load = Number.parseFloat(loadValue ?? 0);
  const normalized = Number.isFinite(load) ? Math.min(100, Math.max(0, load)) : 0;
  return Math.min(5, Math.max(0, Math.floor((normalized + 10) / 20)));
}

export function hasVisibleLoadBars(loadValue) {
  return getFilledBars(loadValue) > 0;
}

export function hasVisibleBodyLoadBars(loadValue) {
  return getFilledBodyBars(loadValue) > 0;
}

export function getVisibleMuscles(muscles) {
  return (muscles || []).filter((muscle) => hasVisibleLoadBars(muscle?.load));
}

export function getVisibleGroups(groups) {
  return (groups || []).filter((group) => hasVisibleLoadBars(group?.load));
}

export function useExerciseLoadDisplay() {
  const openedPartsByKey = ref({});
  const openedGroupsByKey = ref({});

  const getRawPartsList = (exercise) => {
    const parts = exercise?.musc_load?.parts ?? exercise?.parts ?? [];
    return Array.isArray(parts) ? parts : [];
  };

  const isFullBodyPart = (part) =>
    String(part?.id ?? '').trim().toLowerCase() === FULL_BODY_PART_ID;

  const getFullBodyPart = (exercise) => {
    const part = getRawPartsList(exercise).find((p) => isFullBodyPart(p)) || null;
    if (!part || !hasVisibleBodyLoadBars(part.load)) return null;
    return part;
  };

  const getPartsList = (exercise) =>
    getRawPartsList(exercise).filter(
      (p) => !isFullBodyPart(p) && hasVisibleLoadBars(p.load),
    );

  const getPartKey = (part) => part?.id || part?.label || '';
  const getGroupKey = (part, group) =>
    `${getPartKey(part)}::${group?.id || group?.label || ''}`;

  const isPartOpen = (rowKey, part) =>
    (openedPartsByKey.value[rowKey] || new Set()).has(getPartKey(part));
  const isGroupOpen = (rowKey, part, group) =>
    (openedGroupsByKey.value[rowKey] || new Set()).has(getGroupKey(part, group));

  const canExpandPart = (part) =>
    getVisibleMuscles(part?.muscles).length > 0 ||
    getVisibleGroups(part?.groups).length > 0;
  const canExpandGroup = (group) =>
    getVisibleMuscles(group?.muscles).length > 0;

  const togglePart = (rowKey, part) => {
    if (!canExpandPart(part)) return;
    const key = getPartKey(part);
    const prev = openedPartsByKey.value[rowKey] || new Set();
    const nextParts = new Set(prev);
    if (nextParts.has(key)) {
      nextParts.delete(key);
      const prevGroups = openedGroupsByKey.value[rowKey] || new Set();
      const nextGroups = new Set(prevGroups);
      for (const group of part?.groups || []) {
        nextGroups.delete(getGroupKey(part, group));
      }
      openedGroupsByKey.value = { ...openedGroupsByKey.value, [rowKey]: nextGroups };
    } else {
      nextParts.add(key);
    }
    openedPartsByKey.value = { ...openedPartsByKey.value, [rowKey]: nextParts };
  };

  const toggleGroup = (rowKey, part, group) => {
    if (!canExpandGroup(group)) return;
    const key = getGroupKey(part, group);
    const prev = openedGroupsByKey.value[rowKey] || new Set();
    const next = new Set(prev);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    openedGroupsByKey.value = { ...openedGroupsByKey.value, [rowKey]: next };
  };

  return {
    getRawPartsList,
    getFullBodyPart,
    getPartsList,
    getVisibleMuscles,
    getVisibleGroups,
    getPartKey,
    getGroupKey,
    openedPartsByKey,
    openedGroupsByKey,
    isPartOpen,
    isGroupOpen,
    canExpandPart,
    canExpandGroup,
    togglePart,
    toggleGroup,
    getFilledBars,
    getFilledBodyBars,
    hasVisibleLoadBars,
    hasVisibleBodyLoadBars,
  };
}
